import type {GenplanBuilding, GenplanRoom} from '@/sections/genplan';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import {CustomScrollbar} from '@/components/custom-scrollbar/custom-scrollbar';
import roomInfoTemplate from './genplan-room-info.twig';
import {useAddToFavoriteButton} from '@/components/favorite/useAddToFavoriteButton';
import {Utils} from '@/common/scripts/utils';

/**
 * Класс для управления планом помещений
 */
export class GenplanRooms {
    private readonly wrapper: HTMLElement;
    private readonly roomsSchema: HTMLElement;
    private readonly roomsSchemaSvg: SVGElement;
    private readonly roomsSchemaImg: HTMLElement;
    private readonly backToBuildingsBtn: HTMLElement;
    private title: HTMLElement;
    private currentPanel: HTMLElement | null = null;
    private hidePanelTimeout: number | null = null;
    private currentActivePath: SVGPathElement | null = null;
    private currentBuilding: GenplanBuilding | null = null;
    private rooms: GenplanRoom[] = [];
    private scrollbar: CustomScrollbar | null = null;
    private zoomController: { reset(): void } | null = null;

    constructor(wrapper: HTMLElement) {
        this.wrapper = wrapper;
        this.roomsSchema = wrapper.querySelector<HTMLElement>('.j-genplan-rooms')!;
        this.roomsSchemaSvg = wrapper.querySelector<SVGElement>('.j-genplan-rooms-svg')!;
        this.roomsSchemaImg = wrapper.querySelector<HTMLElement>('.j-genplan-rooms-img')!;
        this.backToBuildingsBtn = wrapper.querySelector<HTMLElement>('.j-genplan-back')!;
        this.title = wrapper.querySelector<HTMLElement>('.j-genplan-rooms-title')!;
        this.bindGoToBuildingsButtonEvent();
    }

    /**
     * Устанавливает scrollbar для управления скроллом
     * @param {CustomScrollbar} scrollbar - Экземпляр CustomScrollbar
     */
    public setScrollbar(scrollbar: CustomScrollbar): void {
        this.scrollbar = scrollbar;
    }

    /**
     * Устанавливает zoom controller для сброса зума
     * @param {Object} zoomController - Экземпляр ZoomController
     * @param {Function} zoomController.reset - Метод сброса зума
     */
    public setZoomController(zoomController: { reset(): void }): void {
        this.zoomController = zoomController;
    }

    /**
     * Скроллит схему к первому path помещения
     */
    public scrollToFirstRoom(): void {
        if (!this.scrollbar) {
            return;
        }

        const firstPath = this.roomsSchemaSvg.querySelector<SVGPathElement>('path[id]');


        if (firstPath) {
            this.scrollbar.scrollToElement(firstPath as unknown as HTMLElement);
        }
    }

    /**
     * Показывает план помещений для выбранного здания
     * @param {GenplanBuilding} building - Данные о здании
     */
    public async show(building: GenplanBuilding): Promise<void> {
        this.title!.textContent = building.name;
        this.currentBuilding = building;
        this.rooms = building.rooms;

        // Сбрасываем зум при открытии нового помещения
        this.zoomController?.reset();

        // Центрируем схему
        this.scrollbar?.setCenter();

        // this.roomsSchemaImg.src = building.schemaSrc;
        // this.roomsSchemaImg

        const response = await fetch(building.schemaSrc);
        const svgText = await response.text();

        this.roomsSchemaImg.innerHTML = svgText;

        await this.loadSvgViewBox(building.schemaSrc);

        this.renderRoomPaths();
        this.bindPathEvents();
        this.showSchema();
    }

    /**
     * Скрывает план помещений
     */
    public hide(): void {
        this.clearPaths();
        this.removePanel();
        this.currentBuilding = null;
        this.rooms = [];

        this.hideSchema();
    }

    /**
     * Загружает viewBox из SVG файла и устанавливает его на roomsSchemaSvg
     * @param {string} svgSrc - Путь к SVG файлу
     */
    private async loadSvgViewBox(svgSrc: string): Promise<void> {
        try {
            const response = await fetch(svgSrc);
            const svgText = await response.text();

            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
            const svgElement = svgDoc.querySelector('svg');

            if (!svgElement) {
                console.warn('SVG element not found in loaded content');

                return;
            }

            let viewBox = svgElement.getAttribute('viewBox');

            if (!viewBox) {
                const width = svgElement.getAttribute('width');
                const height = svgElement.getAttribute('height');

                if (width && height) {
                    const cleanWidth = parseFloat(width);
                    const cleanHeight = parseFloat(height);

                    viewBox = `0 0 ${cleanWidth} ${cleanHeight}`;
                }
            }

            if (viewBox) {
                this.roomsSchemaSvg.setAttribute('viewBox', viewBox);
            }
        } catch (error) {
            console.error('Failed to load SVG viewBox:', error);
        }
    }

    /**
     * Рендерит path помещений в SVG
     */
    private renderRoomPaths(): void {
        const pathClass = 'genplan-rooms__path';
        const pathSailedClass = `${pathClass} sailed`;
        const pathReservedClass = `${pathClass} reserved`;

        this.rooms.forEach((room: GenplanRoom) => {
            let path = room.svgPath;
            let cssClass = pathClass;

            if (room.status === 'sailed') {
                cssClass = pathSailedClass;
            } else if (room.status === 'reserved') {
                cssClass = pathReservedClass;
            }

            path = path.replace(/<path/, `<path class="${cssClass}" id="${room.id}"`);

            this.roomsSchemaSvg.insertAdjacentHTML('beforeend', path);
        });
    }

    /**
     * Очищает SVG от всех path помещений
     */
    private clearPaths(): void {
        this.roomsSchemaSvg.querySelectorAll('path').forEach((path: SVGElement) => {
            path.remove();
        });
        this.removeActiveClass();
    }

    /**
     * Показывает схему помещений
     */
    private showSchema(): void {
        this.roomsSchema.style.opacity = '1';
        this.roomsSchema.style.pointerEvents = 'auto';
    }

    /**
     * Скрывает схему помещений
     */
    private hideSchema(): void {
        this.roomsSchemaImg.innerHTML = '';
        this.roomsSchema.style.opacity = '0';
        this.roomsSchema.style.pointerEvents = 'none';
    }

    /**
     * Привязывает обработчик к кнопке "Назад"
     */
    private bindGoToBuildingsButtonEvent(): void {
        this.backToBuildingsBtn.addEventListener('click', () => {
            this.hide();
        });
    }

    /**
     * Привязывает обработчики событий к path помещений
     */
    private bindPathEvents(): void {
        const paths = this.roomsSchemaSvg.querySelectorAll<SVGPathElement>('path[id]');

        paths.forEach((path: SVGPathElement) => {
            path.addEventListener('mouseenter', this.showPanel.bind(this));
            path.addEventListener('mouseleave', this.scheduleHidePanel.bind(this));
            path.addEventListener('click', this.showPanel.bind(this));
        });
    }

    /**
     * Устанавливает активный path помещения
     * @param {SVGPathElement} pathEl - Элемент path помещения
     */
    private setActivePath(pathEl: SVGPathElement): void {
        if (this.currentActivePath && this.currentActivePath !== pathEl) {
            this.currentActivePath.classList.remove('active');
        }

        pathEl.classList.add('active');
        this.currentActivePath = pathEl;
    }

    /**
     * Находит помещение по ID
     * @param {string} roomId - ID помещения
     * @returns {GenplanRoom | undefined} - Помещение или undefined, если не найдено
     */
    private findRoomById(roomId: string): GenplanRoom | undefined {
        return this.rooms.find((room: GenplanRoom) => {
            return room.id === roomId;
        });
    }

    /**
     * Показывает информационную панель помещения
     * @param {MouseEvent} event - Событие мыши
     */
    private showPanel(event: MouseEvent): void {
        const pathEl = event.currentTarget as SVGPathElement;

        this.setActivePath(pathEl);

        const room = this.findRoomById(pathEl.id)!;

        this.cancelHidePanelTimeout();
        this.removePanel();

        const roomInfoHtml = roomInfoTemplate({
            ...room,
            statusLabel: this.getRoomStatusLabel(room)
        });

        this.roomsSchema.insertAdjacentHTML('beforeend', roomInfoHtml);

        this.currentPanel = this.roomsSchema.querySelector<HTMLElement>('.j-genplan-room-info');

        if (this.currentPanel) {
            this.positionPanel(pathEl, this.currentPanel);
            this.bindPanelEvents();
        }

        this.initFavoriteButton(room);
    }

    /**
     * Возвращает человекочитаемый статус помещения.
     * @param {GenplanRoom} room - Данные о помещении
     * @returns {string | null} - Текст статуса или null для доступного помещения
     */
    private getRoomStatusLabel(room: GenplanRoom): string | null {
        switch (room.status) {
            case 'sailed':
                return 'Продано';
            case 'reserved':
                return 'Забронировано';
            default:
                return null;
        }
    }

    /**
     * Позиционирует информационную панель помещения
     * @param {SVGPathElement} pathEl - Элемент path помещения
     * @param {HTMLElement} infoPanel - Элемент информационной панели
     */
    private positionPanel(pathEl: SVGPathElement, infoPanel: HTMLElement): void {
        if (Utils.isMobile()) {
            infoPanel.style.left = `0px`;
            infoPanel.style.bottom = `0px`;

            return;
        }

        const pathRect = pathEl.getBoundingClientRect();
        const roomsSchemaRect = this.roomsSchema.getBoundingClientRect();
        const panelRect = infoPanel.getBoundingClientRect();

        // const offsetX = 70;
        const offsetX = 70;
        // const offsetY = 25;
        const offsetY = 200;

        let left = (pathRect.right - roomsSchemaRect.left) - panelRect.width - offsetX;
        let top = (pathRect.bottom - roomsSchemaRect.top) - panelRect.height - offsetY;

        // Проверка границ
        left = Math.max(0, Math.min(left, roomsSchemaRect.width - panelRect.width));
        top = Math.max(0, Math.min(top, roomsSchemaRect.height - panelRect.height));

        infoPanel.style.left = `${left}px`;
        infoPanel.style.top = `${top}px`;
    }

    /**
     * Привязывает обработчики событий к информационной панели помещения
     */
    private bindPanelEvents(): void {
        if (!this.currentPanel) {
            return;
        }

        this.currentPanel.addEventListener('mouseenter', this.cancelHidePanelTimeout.bind(this));
        this.currentPanel.addEventListener('mouseleave', this.scheduleHidePanel.bind(this));

        const closeBtn = this.currentPanel.querySelector<HTMLElement>('.j-genplan-room-info-close-btn');

        if (closeBtn) {
            closeBtn.addEventListener('click', this.removePanel.bind(this));
        }
    }

    /**
     * Запускает таймер на скрытие информационной панели помещения
     */
    private scheduleHidePanel(): void {
        this.cancelHidePanelTimeout();
        this.hidePanelTimeout = window.setTimeout(() => {
            this.removePanel();
            this.removeActiveClass();
        }, 100);
    }

    /**
     * Отменяет таймер скрытия информационной панели помещения
     */
    private cancelHidePanelTimeout(): void {
        if (this.hidePanelTimeout !== null) {
            clearTimeout(this.hidePanelTimeout);
            this.hidePanelTimeout = null;
        }
    }

    /**
     * Удаляет информационную панель помещения
     */
    private removePanel(): void {
        if (this.currentPanel) {
            this.currentPanel.remove();
            this.currentPanel = null;
        }
    }

    /**
     * Удаляет класс active с текущего активного пути
     */
    private removeActiveClass(): void {
        if (this.currentActivePath) {
            this.currentActivePath.classList.remove('active');
            this.currentActivePath = null;
        }
    }

    /**
     * Инициализирует кнопку добавления в избранное
     * @param {GenplanRoom} room - Данные о помещении
     */
    private initFavoriteButton(room: GenplanRoom): void {
        const button = this.roomsSchema.querySelector<HTMLElement>('.j-favorite-btn');

        if (!button) {
            return;
        }

        useAddToFavoriteButton(button);

        button.addEventListener('click', () => {
            room.inFavorite = !room.inFavorite;
        });
    }
}
